/**
 * i18next setup — English + Spanish. Covers:
 *  - `common.*`: shared keys used by components copied from PayrollSystem
 *    (data-table pagination, dialog/sheet close buttons, loading spinner)
 *  - `appShell.*` / `flowList.*` / `flowPlayer.*`: the Staff-facing UI chrome
 *    (browse + player screens, and the nav shared with Admin)
 *
 * Scope: Admin-only tooling (Staff/Categories management, the script editor)
 * and the pre-login pages stay English-only — those are internal
 * tooling and there's no known user before login to have a preference for.
 * The interface language here follows `user.language_preference`: EN → en,
 * ES → es, BOTH → en (content itself still renders in both languages —
 * see FlowPlayerPage/FlowListPage — "BOTH" just isn't a sensible *interface*
 * language, you can't show two languages of one button label at once).
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";

const en = {
  common: {
    prev: "Previous",
    next: "Next",
    loading: "Loading…",
    close: "Close",
    page_of: "Page {{current}} of {{total}}",
    rows_count: "{{count}} rows",
  },
  role: {
    ADMIN: "Admin",
    STAFF: "Staff",
  },
  appShell: {
    flows: "Scripts",
    guides: "Guides",
    staff: "Staff",
    categories: "Categories",
    callLog: "Call log",
    auditLog: "Audit log",
    logout: "Log out",
    openMenu: "Open menu",
    language: "Language",
    languageEn: "English",
    languageEs: "Spanish",
    languageBoth: "English & Spanish",
  },
  guideList: {
    title: "Guides",
    subtitle:
      "FAQs and guidance for situations that don't fit a step-by-step script — difficult callers, edge cases, and anything else worth looking up mid-call.",
    searchPlaceholder: "Search guides…",
    allCategories: "All",
    allTypes: "All types",
    typeGuideline: "Guidelines",
    typeRebuttal: "Rebuttals",
    guidanceLabel: "Guidance — don't read aloud",
    noContent: "This entry has no content yet.",
    noEntriesTitle: "No guide entries yet",
    noEntriesDescription: "Once an admin adds one, it'll show up here.",
    noMatchesTitle: "No matching guides",
    noMatchesDescription: "Try a different search term, or clear the category filter.",
    loadError: "Couldn't load guides.",
    notesHeading: "Notes & Links",
    notesAdd: "Add",
    notesEdit: "Edit",
    notesEmpty: "Nothing here yet — add a link or a reminder for next time.",
    notesPlaceholder: "Paste a link, jot a reminder… only you'll see this.",
    notesSave: "Save",
    notesCancel: "Cancel",
    notesSaveError: "Couldn't save your note.",
  },
  guidePanel: {
    openFullPage: "Full page",
    categoryFilterLabel: "Filter guides by category",
  },
  flowList: {
    title: "Scripts",
    subtitle: "Find the process for your customer's situation and walk through it step by step.",
    searchPlaceholder: "Search scripts…",
    allCategories: "All",
    noFlowsTitle: "No scripts published yet",
    noFlowsDescription: "Once an admin publishes a script, it'll show up here.",
    noMatchesTitle: "No matching scripts",
    noMatchesDescription: "Try a different search term, or clear the category filter.",
    loadError: "Couldn't load scripts.",
  },
  flowPlayer: {
    startOver: "Start over",
    backToFlows: "Back to scripts",
    back: "Back",
    stepsTaken: "Steps taken",
    flowComplete: "Script complete",
    sayThis: "Say this",
    decisionPoint: "Question",
    textEntryPoint: "Type the answer",
    clientInfoPoint: "Client information",
    freeTextPlaceholder: "Type what the customer said…",
    clientInfoHeading: "Client information",
    clientInfoFirstName: "First name",
    clientInfoLastName: "Last name",
    clientInfoDob: "Date of birth",
    clientInfoEmail: "Email",
    clientInfoPhone: "Phone number",
    clientInfoDeclined: "Client declined to share — use placeholder",
    clientInfoDeclinedBadge: "Declined to share — placeholder used",
    internalNoteLabel: "Internal note — don't read aloud",
    noBranchConfigured: "This step has no next branch configured. Let an admin know.",
    continueLabel: "Continue",
    notAvailableTitle: "This script isn't available",
    notAvailableDescription:
      "It may have been unpublished. Ask an admin, or go back to browse other scripts.",
    loadErrorTitle: "Couldn't load this script",
    loadErrorDescription: "Try again in a moment.",
    spanishNotAvailable: "(Spanish not available for this step — showing English.)",
    personalizeThis: "Personalize this",
    editPersonalization: "Edit your wording",
    resetToScript: "Reset to script",
    personalizedBadge: "Personalized",
    personalizeEnglishLabel: "Your wording (English)",
    personalizeSpanishLabel: "Your wording (Spanish)",
    save: "Save",
    cancel: "Cancel",
    personalizationSaved: "Saved — you'll see your own wording here from now on.",
    personalizationSaveError: "Couldn't save your personalization.",
    personalizationReset: "Reset to the script's original wording.",
    personalizationResetError: "Couldn't reset your personalization.",
    reorderHint: "Drag, or use the arrows, to reorder — only you see this order.",
    personalizedOrderBadge: "Personalized order",
    resetOrder: "Reset order",
    orderReset: "Reset to the script's original order.",
    moveUp: "Move up",
    moveDown: "Move down",
    stepOrderButton: "My step order",
    stepOrderTitle: "My step order",
    stepOrderDescription:
      "Drag steps into the order you like to work through this script — this is only your own private order. It doesn't change what each step's own buttons lead to. Whatever's on top is where a fresh call starts; tap any step to jump straight there now.",
    stepOrderUntitled: "(untitled)",
    stepOrderStartBadge: "Call starts here",
    stepOrderSaveError: "Couldn't save your step order.",
    stepOrderReset: "Reset to the script's original step order.",
    stepOrderResetError: "Couldn't reset your step order.",
    summaryHeading: "Call summary",
    summaryTimestamp: "Completed",
    summaryOutcome: "Outcome",
    summaryNoSteps: "No steps were recorded for this call.",
    copySummary: "Copy",
    downloadSummary: "Download",
    summaryCopied: "Copied to clipboard.",
    summaryCopyError: "Couldn't copy to clipboard.",
    dismissSummary: "Dismiss",
    capturedSoFarHeading: "Notes",
    liveNotesDobAbbrev: "DOB",
    completeCall: "Complete call",
    completeCallOutcome: "Completed",
    callCompletedLogged: "Call completed and logged.",
    endCall: "End call",
    callLogSaved: "Call ended and logged.",
    callLogSaveError: "Couldn't save this to the call log.",
    endCallDialogTitle: "End this call?",
    endCallDialogDescription:
      "This logs the call as ended early — the steps already taken stay on the record. The script resets to the first step right after, ready for the next call.",
    endCallReasonLabel: "Reason",
    endCallReasonPlaceholder: "Choose a reason",
    endCallReasonCustomerDisconnected: "Customer disconnected",
    endCallReasonLostSignal: "Lost signal",
    endCallReasonOther: "Other",
    endCallNoteLabel: "Note (optional)",
    endCallNotePlaceholder: "Any extra context for whoever reviews this later…",
    endCallNotePiiWarning: "Don't include the customer's name, number, or other personal details.",
  },
};

const es: typeof en = {
  common: {
    prev: "Anterior",
    next: "Siguiente",
    loading: "Cargando…",
    close: "Cerrar",
    page_of: "Página {{current}} de {{total}}",
    rows_count: "{{count}} filas",
  },
  role: {
    ADMIN: "Administrador",
    STAFF: "Agente",
  },
  appShell: {
    flows: "Guiones",
    guides: "Guías",
    staff: "Personal",
    categories: "Categorías",
    callLog: "Registro de llamadas",
    auditLog: "Registro de auditoría",
    logout: "Cerrar sesión",
    openMenu: "Abrir menú",
    language: "Idioma",
    languageEn: "Inglés",
    languageEs: "Español",
    languageBoth: "Inglés y español",
  },
  guideList: {
    title: "Guías",
    subtitle:
      "Preguntas frecuentes y orientación para situaciones que no encajan en un guion paso a paso — clientes difíciles, casos excepcionales y cualquier otra cosa que valga la pena consultar durante una llamada.",
    searchPlaceholder: "Buscar guías…",
    allCategories: "Todas",
    allTypes: "Todos los tipos",
    typeGuideline: "Pautas",
    typeRebuttal: "Contraargumentos",
    guidanceLabel: "Orientación — no leer en voz alta",
    noContent: "Esta entrada aún no tiene contenido.",
    noEntriesTitle: "Aún no hay guías",
    noEntriesDescription: "Cuando un administrador agregue una, aparecerá aquí.",
    noMatchesTitle: "No se encontraron guías",
    noMatchesDescription: "Pruebe con otro término de búsqueda o quite el filtro de categoría.",
    loadError: "No se pudieron cargar las guías.",
    notesHeading: "Notas y enlaces",
    notesAdd: "Agregar",
    notesEdit: "Editar",
    notesEmpty: "Nada aquí todavía — agregue un enlace o un recordatorio para la próxima vez.",
    notesPlaceholder: "Pegue un enlace, anote un recordatorio… solo usted lo verá.",
    notesSave: "Guardar",
    notesCancel: "Cancelar",
    notesSaveError: "No se pudo guardar su nota.",
  },
  guidePanel: {
    openFullPage: "Página completa",
    categoryFilterLabel: "Filtrar guías por categoría",
  },
  flowList: {
    title: "Guiones",
    subtitle: "Encuentre el proceso para la situación de su cliente y sígalo paso a paso.",
    searchPlaceholder: "Buscar guiones…",
    allCategories: "Todos",
    noFlowsTitle: "Aún no hay guiones publicados",
    noFlowsDescription: "Cuando un administrador publique un guion, aparecerá aquí.",
    noMatchesTitle: "No se encontraron guiones",
    noMatchesDescription: "Pruebe con otro término de búsqueda o quite el filtro de categoría.",
    loadError: "No se pudieron cargar los guiones.",
  },
  flowPlayer: {
    startOver: "Empezar de nuevo",
    backToFlows: "Volver a guiones",
    back: "Atrás",
    stepsTaken: "Pasos realizados",
    flowComplete: "Guion completado",
    sayThis: "Diga esto",
    decisionPoint: "Pregunta",
    textEntryPoint: "Escriba la respuesta",
    clientInfoPoint: "Información del cliente",
    freeTextPlaceholder: "Escriba lo que dijo el cliente…",
    clientInfoHeading: "Información del cliente",
    clientInfoFirstName: "Nombre",
    clientInfoLastName: "Apellido",
    clientInfoDob: "Fecha de nacimiento",
    clientInfoEmail: "Correo electrónico",
    clientInfoPhone: "Número de teléfono",
    clientInfoDeclined: "El cliente no quiso compartir — usar marcador de posición",
    clientInfoDeclinedBadge: "No quiso compartir — se usó un marcador de posición",
    internalNoteLabel: "Nota interna — no leer en voz alta",
    noBranchConfigured: "Este paso no tiene una rama configurada. Avise a un administrador.",
    continueLabel: "Continuar",
    notAvailableTitle: "Este guion no está disponible",
    notAvailableDescription:
      "Puede que se haya despublicado. Consulte a un administrador o vuelva a explorar otros guiones.",
    loadErrorTitle: "No se pudo cargar este guion",
    loadErrorDescription: "Intente de nuevo en un momento.",
    spanishNotAvailable: "(Español no disponible para este paso — mostrando inglés.)",
    personalizeThis: "Personalizar esto",
    editPersonalization: "Editar tu texto",
    resetToScript: "Restablecer al guion",
    personalizedBadge: "Personalizado",
    personalizeEnglishLabel: "Tu texto (inglés)",
    personalizeSpanishLabel: "Tu texto (español)",
    save: "Guardar",
    cancel: "Cancelar",
    personalizationSaved: "Guardado — de ahora en adelante verás tu propio texto aquí.",
    personalizationSaveError: "No se pudo guardar tu personalización.",
    personalizationReset: "Restablecido al texto original del guion.",
    personalizationResetError: "No se pudo restablecer tu personalización.",
    reorderHint: "Arrastre, o use las flechas, para reordenar — solo usted ve este orden.",
    personalizedOrderBadge: "Orden personalizado",
    resetOrder: "Restablecer orden",
    orderReset: "Restablecido al orden original del guion.",
    moveUp: "Subir",
    moveDown: "Bajar",
    stepOrderButton: "Mi orden de pasos",
    stepOrderTitle: "Mi orden de pasos",
    stepOrderDescription:
      "Arrastre los pasos al orden en que prefiera trabajar este guion — este es solo su propio orden privado. No cambia a dónde llevan los botones de cada paso. El que esté arriba es donde empieza una llamada nueva; toque cualquier paso para ir directamente ahí ahora.",
    stepOrderUntitled: "(sin título)",
    stepOrderStartBadge: "La llamada empieza aquí",
    stepOrderSaveError: "No se pudo guardar tu orden de pasos.",
    stepOrderReset: "Restablecido al orden de pasos original del guion.",
    stepOrderResetError: "No se pudo restablecer tu orden de pasos.",
    summaryHeading: "Resumen de la llamada",
    summaryTimestamp: "Completado",
    summaryOutcome: "Resultado",
    summaryNoSteps: "No se registraron pasos para esta llamada.",
    copySummary: "Copiar",
    downloadSummary: "Descargar",
    summaryCopied: "Copiado al portapapeles.",
    summaryCopyError: "No se pudo copiar al portapapeles.",
    dismissSummary: "Cerrar",
    capturedSoFarHeading: "Notas",
    liveNotesDobAbbrev: "F. nac.",
    completeCall: "Completar llamada",
    completeCallOutcome: "Completada",
    callCompletedLogged: "Llamada completada y registrada.",
    endCall: "Finalizar llamada",
    callLogSaved: "Llamada finalizada y registrada.",
    callLogSaveError: "No se pudo guardar en el registro de llamadas.",
    endCallDialogTitle: "¿Finalizar esta llamada?",
    endCallDialogDescription:
      "Esto registra la llamada como finalizada antes de tiempo — los pasos ya realizados quedan en el registro. El guion vuelve al primer paso justo después, listo para la próxima llamada.",
    endCallReasonLabel: "Motivo",
    endCallReasonPlaceholder: "Elija un motivo",
    endCallReasonCustomerDisconnected: "El cliente se desconectó",
    endCallReasonLostSignal: "Se perdió la señal",
    endCallReasonOther: "Otro",
    endCallNoteLabel: "Nota (opcional)",
    endCallNotePlaceholder: "Cualquier contexto adicional para quien revise esto después…",
    endCallNotePiiWarning:
      "No incluya el nombre, número u otros datos personales del cliente.",
  },
};

i18n.use(initReactI18next).init({
  lng: "en",
  fallbackLng: "en",
  defaultNS: "common",
  ns: ["common"],
  resources: {
    en: { common: en },
    es: { common: es },
  },
  interpolation: {
    escapeValue: false, // React already escapes
  },
});

export { i18n };
